import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Eye, EyeOff, AlertCircle, CheckCircle, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ROUTES } from '@/config/routes';
import { PasswordResetService, PasswordResetServiceError } from '@/services/passwordResetService';

interface FormErrors {
  newPassword?: string;
  confirmPassword?: string;
  general?: string;
}

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
}

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    label: '',
    color: '',
  });

  // Helper functions for translations
  const getResetPassword = (key: string) => t(`auth.resetPassword.${key}`);
  const getResetPasswordForm = (key: string) => t(`auth.resetPassword.form.${key}`);
  const getResetPasswordSuccess = (key: string) => t(`auth.resetPassword.success.${key}`);
  const getResetPasswordErrors = (key: string) => t(`auth.resetPassword.errors.${key}`);
  const getResetPasswordSecurity = (key: string) => t(`auth.resetPassword.security.${key}`);

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (!tokenFromUrl) {
      setFormErrors({ general: getResetPasswordErrors('tokenInvalid') });
    } else {
      setToken(tokenFromUrl);
    }
  }, [searchParams, getResetPasswordErrors]);

  const calculatePasswordStrength = (password: string): PasswordStrength => {
    if (!password) return { score: 0, label: '', color: '' };

    let score = 0;
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      numbers: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    score += checks.length ? 2 : 0;
    score += checks.uppercase ? 1 : 0;
    score += checks.lowercase ? 1 : 0;
    score += checks.numbers ? 1 : 0;
    score += checks.special ? 1 : 0;

    if (score <= 2) {
      return {
        score,
        label: getResetPasswordSecurity('weak'),
        color: 'text-red-400',
      };
    } else if (score <= 4) {
      return {
        score,
        label: getResetPasswordSecurity('medium'),
        color: 'text-yellow-400',
      };
    } else {
      return {
        score,
        label: getResetPasswordSecurity('strong'),
        color: 'text-green-400',
      };
    }
  };

  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(newPassword));
  }, [newPassword, getResetPasswordSecurity]);

  const validatePassword = (password: string): string | null => {
    if (!password.trim()) {
      return getResetPasswordErrors('passwordRequired');
    }

    if (password.length < 8) {
      return getResetPasswordErrors('passwordMinLength');
    }

    return null;
  };

  const validateConfirmPassword = (password: string, confirmPwd: string): string | null => {
    if (password !== confirmPwd) {
      return getResetPasswordErrors('passwordsDoNotMatch');
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setFormErrors({});

    // Validate token
    if (!token) {
      setFormErrors({ general: getResetPasswordErrors('tokenInvalid') });
      return;
    }

    // Validate passwords
    const passwordError = validatePassword(newPassword);
    const confirmPasswordError = validateConfirmPassword(newPassword, confirmPassword);

    if (passwordError || confirmPasswordError) {
      setFormErrors({
        newPassword: passwordError || undefined,
        confirmPassword: confirmPasswordError || undefined,
      });
      return;
    }

    setIsLoading(true);

    try {
      await PasswordResetService.confirmPasswordReset(token, newPassword);
      setIsSuccess(true);
    } catch (error) {
      if (error instanceof PasswordResetServiceError) {
        if (error.status === 400) {
          if (error.message.includes('expired') || error.message.includes('invalid')) {
            setFormErrors({ general: getResetPasswordErrors('tokenExpired') });
          } else {
            setFormErrors({ general: getResetPasswordErrors('tokenInvalid') });
          }
        } else {
          setFormErrors({ general: getResetPasswordErrors('updateFailed') });
        }
      } else {
        setFormErrors({ general: getResetPasswordErrors('updateFailed') });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = () => {
    navigate(ROUTES.AUTH.LOGIN);
  };

  if (isSuccess) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 px-4 sm:px-6 lg:px-8">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 to-blue-600/20 backdrop-blur-3xl" />
        <div className="absolute inset-0 bg-[url('/images/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]" />

        <motion.div
          className="relative w-full max-w-md space-y-8 rounded-2xl border border-slate-700/50 bg-slate-800/80 p-8 shadow-2xl backdrop-blur-xl"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Success Icon */}
          <motion.div
            className="flex justify-center"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="flex size-16 items-center justify-center rounded-full bg-green-500/20">
              <CheckCircle className="size-8 text-green-400" />
            </div>
          </motion.div>

          {/* Success Content */}
          <motion.div
            className="space-y-4 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h1 className="text-2xl font-bold text-white">{getResetPasswordSuccess('title')}</h1>
            <p className="text-slate-300">{getResetPasswordSuccess('subtitle')}</p>
            <div className="rounded-lg border border-slate-600 bg-slate-700/50 p-4 text-sm text-slate-300">
              <Shield className="mr-2 inline size-4" />
              {getResetPasswordSuccess('instructions')}
            </div>
          </motion.div>

          {/* Sign In Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Button
              onClick={handleSignIn}
              className="w-full rounded-lg bg-gradient-to-r from-primary-600 to-primary-500 py-3 font-semibold text-white shadow-lg transition-all duration-200 hover:from-primary-700 hover:to-primary-600 hover:shadow-xl focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
            >
              {getResetPasswordSuccess('signInButton')}
            </Button>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 px-4 sm:px-6 lg:px-8">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 to-blue-600/20 backdrop-blur-3xl" />
      <div className="absolute inset-0 bg-[url('/images/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]" />

      <motion.div
        className="relative w-full max-w-md space-y-8 rounded-2xl border border-slate-700/50 bg-slate-800/80 p-8 shadow-2xl backdrop-blur-xl"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Header */}
        <motion.div
          className="space-y-2 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h1 className="text-3xl font-bold text-white">{getResetPassword('title')}</h1>
          <p className="text-slate-300">{getResetPassword('subtitle')}</p>
        </motion.div>

        {/* Error Display */}
        {formErrors.general && (
          <motion.div
            className="flex items-center rounded-lg border border-red-500/50 bg-red-500/20 p-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            role="alert"
            aria-live="polite"
          >
            <AlertCircle className="mr-3 size-5 shrink-0 text-red-400" />
            <span className="text-sm text-red-300">{formErrors.general}</span>
          </motion.div>
        )}

        {/* Form */}
        <motion.form
          onSubmit={handleSubmit}
          className="space-y-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          noValidate
        >
          {/* New Password Field */}
          <div className="relative">
            <div className="relative">
              <Input
                type={showNewPassword ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                value={newPassword}
                placeholder=" "
                required
                onChange={e => setNewPassword(e.target.value)}
                className={`peer w-full rounded-lg border bg-slate-800/50 px-4 py-3 pr-12 text-white backdrop-blur-sm transition-colors placeholder:text-transparent focus:outline-none ${
                  formErrors.newPassword
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-slate-600 focus:border-primary-500'
                }`}
                aria-describedby={formErrors.newPassword ? 'newPassword-error' : undefined}
              />
              <label
                htmlFor="newPassword"
                className="text-shadow-sm pointer-events-none absolute left-4 top-3 z-10 origin-left text-slate-400 transition-all duration-200 ease-out [text-shadow:_0_1px_3px_rgb(15_23_42_/_0.8)] peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-8 peer-focus:scale-75 peer-focus:text-primary-400 peer-[:not(:placeholder-shown)]:-translate-y-8 peer-[:not(:placeholder-shown)]:scale-75 peer-[:not(:placeholder-shown)]:text-slate-300"
              >
                {getResetPasswordForm('newPasswordLabel')}
              </label>
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-3 text-slate-400 transition-colors hover:text-primary-400 focus:text-primary-400 focus:outline-none"
                aria-label={showNewPassword ? 'Hide password' : 'Show password'}
              >
                {showNewPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
              </button>
            </div>

            {/* Password Strength Indicator */}
            {newPassword && (
              <div className="mt-2 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">
                    {getResetPasswordSecurity('strengthIndicator')}
                  </span>
                  <span className={passwordStrength.color}>{passwordStrength.label}</span>
                </div>
                <div className="h-2 w-full rounded-full bg-slate-700">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      passwordStrength.score <= 2
                        ? 'bg-red-500'
                        : passwordStrength.score <= 4
                          ? 'bg-yellow-500'
                          : 'bg-green-500'
                    }`}
                    style={{ width: `${(passwordStrength.score / 6) * 100}%` }}
                  />
                </div>
                <p className="text-xs text-slate-400">{getResetPasswordSecurity('requirements')}</p>
              </div>
            )}

            {formErrors.newPassword && (
              <p id="newPassword-error" className="mt-2 flex items-center text-sm text-red-400">
                <AlertCircle className="mr-1 size-4" />
                {formErrors.newPassword}
              </p>
            )}
          </div>

          {/* Confirm Password Field */}
          <div className="relative">
            <div className="relative">
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={confirmPassword}
                placeholder=" "
                required
                onChange={e => setConfirmPassword(e.target.value)}
                className={`peer w-full rounded-lg border bg-slate-800/50 px-4 py-3 pr-12 text-white backdrop-blur-sm transition-colors placeholder:text-transparent focus:outline-none ${
                  formErrors.confirmPassword
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-slate-600 focus:border-primary-500'
                }`}
                aria-describedby={formErrors.confirmPassword ? 'confirmPassword-error' : undefined}
              />
              <label
                htmlFor="confirmPassword"
                className="text-shadow-sm pointer-events-none absolute left-4 top-3 z-10 origin-left text-slate-400 transition-all duration-200 ease-out [text-shadow:_0_1px_3px_rgb(15_23_42_/_0.8)] peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-8 peer-focus:scale-75 peer-focus:text-primary-400 peer-[:not(:placeholder-shown)]:-translate-y-8 peer-[:not(:placeholder-shown)]:scale-75 peer-[:not(:placeholder-shown)]:text-slate-300"
              >
                {getResetPasswordForm('confirmPasswordLabel')}
              </label>
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-3 text-slate-400 transition-colors hover:text-primary-400 focus:text-primary-400 focus:outline-none"
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
              </button>
            </div>
            {formErrors.confirmPassword && (
              <p id="confirmPassword-error" className="mt-2 flex items-center text-sm text-red-400">
                <AlertCircle className="mr-1 size-4" />
                {formErrors.confirmPassword}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isLoading || !token}
            className="w-full rounded-lg bg-gradient-to-r from-primary-600 to-primary-500 py-3 font-semibold text-white shadow-lg transition-all duration-200 hover:from-primary-700 hover:to-primary-600 hover:shadow-xl focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="mr-2 size-5 animate-spin rounded-full border-b-2 border-white"></div>
                {getResetPasswordForm('submittingButton')}
              </div>
            ) : (
              getResetPasswordForm('submitButton')
            )}
          </Button>
        </motion.form>

        {/* Back to Login */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Link
            to={ROUTES.AUTH.LOGIN}
            className="inline-flex items-center rounded-md px-1 text-sm text-primary-400 transition-colors hover:text-primary-300 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900"
          >
            <ArrowLeft className="mr-1 size-4" />
            {getResetPasswordForm('backToLogin')}
          </Link>
        </motion.div>
      </motion.div>
    </div>
  );
}
